import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ClipboardCheck, Clock, DollarSign, HeartPulse,
  ShieldCheck, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import Seo from '../components/Seo';

// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_URL = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const minDateLocalString = () => {
  // 24 hours from now, formatted for <input type="datetime-local">.
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const HireCNAPage = () => {
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState({ hourly_rate_usd: 25, min_hours: 4, advance_notice_hours: 24 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { booking_id }

  const minStart = useMemo(() => minDateLocalString(), []);

  const [form, setForm] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    service_address: '',
    requested_start: '',
    hours: 4,
    services: [],
    notes: '',
    typed_signature: '',
    agreed: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/hire-cna/services`);
        const data = await r.json();
        setServices(data.services || []);
        setConfig({
          hourly_rate_usd: data.hourly_rate_usd,
          min_hours: data.min_hours,
          advance_notice_hours: data.advance_notice_hours,
        });
      } catch (e) {
        setError('Unable to load caregiver services. Please refresh.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-confirm if returning from a successful Stripe checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const bookingId = params.get('booking_id');
    if (sessionId && bookingId) {
      (async () => {
        try {
          await fetch(`${API_URL}/api/hire-cna/confirm?session_id=${encodeURIComponent(sessionId)}`, { method: 'POST' });
        } catch (e) { /* ignore — booking still exists, admin can resolve */ }
        setSuccess({ booking_id: bookingId });
        window.history.replaceState({}, '', '/hire-cna');
      })();
    }
  }, []);

  const totalDollars = (Number(form.hours) || 0) * config.hourly_rate_usd;

  const toggleService = (svc) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(svc)
        ? f.services.filter((s) => s !== svc)
        : [...f.services, svc],
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    if (!form.client_name.trim()) return 'Please enter your full name.';
    if (!form.client_phone.trim()) return 'Phone number is required.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.client_email)) return 'Please enter a valid email address.';
    if (!form.service_address.trim()) return 'Service address is required.';
    if (!form.requested_start) return 'Please choose a requested start date & time.';
    const startMs = new Date(form.requested_start).getTime();
    if (Number.isNaN(startMs)) return 'Invalid start date/time.';
    if (startMs < Date.now() + config.advance_notice_hours * 60 * 60 * 1000 - 60000) {
      return `Bookings require at least ${config.advance_notice_hours}-hour advance notice.`;
    }
    const hrs = Number(form.hours);
    if (!Number.isFinite(hrs) || hrs < config.min_hours) {
      return `Minimum booking is ${config.min_hours} hours.`;
    }
    if (form.services.length === 0) return 'Please select at least one caregiver service.';
    if (!form.typed_signature.trim()) return 'Please type your full legal name as your electronic signature.';
    if (!form.agreed) return 'You must agree to the Service Agreement and indemnity terms before checkout.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) { setError(v); return; }

    setSubmitting(true);
    try {
      // Convert local datetime to ISO with TZ offset
      const startISO = new Date(form.requested_start).toISOString();
      const r = await fetch(`${API_URL}/api/hire-cna/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: form.client_name.trim(),
          client_phone: form.client_phone.trim(),
          client_email: form.client_email.trim(),
          service_address: form.service_address.trim(),
          requested_start: startISO,
          hours: Number(form.hours),
          services: form.services,
          notes: form.notes.trim(),
          typed_signature: form.typed_signature.trim(),
          success_url: `${window.location.origin}/hire-cna`,
          cancel_url: `${window.location.origin}/hire-cna`,
        }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || 'Unable to start checkout. Please try again.');
      }
      const data = await r.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Checkout session was not created. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="hire-cna-success" style={{ maxWidth: 720, margin: '60px auto', padding: 24, fontFamily: 'system-ui, sans-serif' }}>
        <CheckCircle2 size={56} color="#16a34a" style={{ marginBottom: 12 }} />
        <h1 style={{ color: '#1e3a5f' }}>Booking Confirmed</h1>
        <p style={{ color: '#475569', lineHeight: 1.6 }}>
          Thank you — your booking <b>{success.booking_id}</b> has been paid in full and a MedCare coordinator
          will contact you within one business day to confirm the assigned caregiver.
        </p>
        <a
          href={`${API_URL}/api/hire-cna/contract/${success.booking_id}`}
          target="_blank"
          rel="noreferrer"
          data-testid="download-cna-contract"
          style={{ display: 'inline-block', marginTop: 16, background: '#1e3a8a', color: 'white', padding: '12px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700 }}
        >
          Download Service Agreement (PDF)
        </a>
        <div style={{ marginTop: 28 }}>
          <Link to="/" data-testid="success-back-home" style={{ color: '#1e40af', textDecoration: 'underline' }}>← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="hire-cna-page" style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
      <Seo
        title="Hire a Certified Nurse Assistant · $25/hour · MedCare Memphis"
        description="On-demand in-home CNA care in Memphis, TN. Billed up front at $25/hour with a 4-hour minimum and 24-hour advance notice. Personal care, meal prep, medication reminders, transportation, respite & more. Signed online Service Agreement."
        canonical="https://medcaretrainingacademy.com/hire-cna"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "How much does it cost to hire a CNA?",
              "acceptedAnswer": { "@type": "Answer", "text": "$25 per hour, billed up front via Stripe checkout." } },
            { "@type": "Question", "name": "What is the minimum booking?",
              "acceptedAnswer": { "@type": "Answer", "text": "4 hours minimum per visit." } },
            { "@type": "Question", "name": "How much notice do I need to give?",
              "acceptedAnswer": { "@type": "Answer", "text": "All bookings require at least 24 hours advance notice." } },
            { "@type": "Question", "name": "What services do MedCare caregivers provide?",
              "acceptedAnswer": { "@type": "Answer", "text": "Non-medical companion and personal-care services including bathing/grooming, meal preparation, medication reminders, mobility assistance, light housekeeping, companionship, vital signs monitoring, transportation to appointments, respite care, and post-surgery recovery support." } },
            { "@type": "Question", "name": "Is there a contract?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every booking includes a signed online Service Agreement with full indemnity terms, generated as a downloadable PDF after payment." } }
          ]
        }}
      />
      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', color: 'white', padding: '24px 40px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Link to="/" data-testid="hire-cna-back-home" style={{ color: '#fbbf24', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={18} /> Back to Home
          </Link>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>Memphis · 901.625.1201</div>
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: '20px auto 0', textAlign: 'center' }}>
          <HeartPulse size={42} color="#fbbf24" />
          <h1 data-testid="hire-cna-h1" style={{ fontSize: '2.4rem', margin: '8px 0 6px' }}>MedCare Nursing &amp; Transportation Services</h1>
          <p style={{ color: '#fbbf24', fontWeight: 700, letterSpacing: 0.5, margin: '0 0 10px', textTransform: 'uppercase', fontSize: '0.85rem' }}>
            Hire a Certified Nurse Assistant
          </p>
          <p style={{ color: '#cbd5e1', maxWidth: 720, margin: '0 auto', lineHeight: 1.5 }}>
            On-demand non-medical CNA care in your home — billed up front at <b style={{ color: '#fbbf24' }}>${config.hourly_rate_usd}/hour</b>,
            with a <b>{config.min_hours}-hour minimum</b> and <b>{config.advance_notice_hours}-hour advance notice</b>.
            All bookings include a signed online Service Agreement.
          </p>
          <div style={{ marginTop: 14, display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <a
              href={`${API_URL}/api/hire-cna/qr-code.png`}
              target="_blank"
              rel="noreferrer"
              data-testid="hire-cna-qr-download"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#fbbf24', padding: '8px 14px', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem', border: '1px solid rgba(251,191,36,0.4)' }}
            >
              Download QR code
            </a>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 24 }} className="hire-cna-grid">
        {/* FORM */}
        <form onSubmit={handleSubmit} style={{ background: 'white', padding: 24, borderRadius: 14, boxShadow: '0 4px 14px -8px rgba(15,23,42,0.18)' }}>
          {error && (
            <div data-testid="hire-cna-error" style={{ background: '#fef2f2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={18} /> {error}
            </div>
          )}

          <h2 style={{ color: '#1e3a5f', marginTop: 0 }}>Your Information</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Full Legal Name" name="client_name" value={form.client_name} onChange={handleChange} testid="cna-input-name" required />
            <Field label="Phone Number" name="client_phone" value={form.client_phone} onChange={handleChange} testid="cna-input-phone" required />
            <Field label="Email Address" name="client_email" type="email" value={form.client_email} onChange={handleChange} testid="cna-input-email" required />
            <Field label="Service Address" name="service_address" value={form.service_address} onChange={handleChange} testid="cna-input-address" required />
          </div>

          <h2 style={{ color: '#1e3a5f', marginTop: 24 }}>Scheduling &amp; Hours</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>Requested Start Date &amp; Time <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="datetime-local"
                name="requested_start"
                value={form.requested_start}
                onChange={handleChange}
                min={minStart}
                data-testid="cna-input-start"
                style={inputStyle}
                required
              />
              <div style={hintStyle}>Earliest available: {minStart.replace('T', ' ')} (your local time). 24-hour notice enforced.</div>
            </div>
            <div>
              <label style={labelStyle}>Hours to Book <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                type="number"
                name="hours"
                min={config.min_hours}
                step={1}
                value={form.hours}
                onChange={handleChange}
                data-testid="cna-input-hours"
                style={inputStyle}
                required
              />
              <div style={hintStyle}>Minimum {config.min_hours} hours. Billed up front at ${config.hourly_rate_usd}/hour.</div>
            </div>
          </div>

          <h2 style={{ color: '#1e3a5f', marginTop: 24 }}>Caregiver Services Needed</h2>
          {loading ? (
            <p style={{ color: '#64748b' }}>Loading services…</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
              {services.map((svc) => {
                const active = form.services.includes(svc);
                const tid = `cna-service-${svc.split(' ')[0].toLowerCase().replace(/[^a-z]/g, '')}`;
                return (
                  <label
                    key={svc}
                    data-testid={tid}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12,
                      border: `2px solid ${active ? '#1e40af' : '#e5e7eb'}`,
                      borderRadius: 10, cursor: 'pointer',
                      background: active ? '#eff6ff' : 'white',
                      transition: 'all 120ms ease',
                    }}
                  >
                    <input type="checkbox" checked={active} onChange={() => toggleService(svc)} style={{ marginTop: 3 }} />
                    <span style={{ fontSize: '0.92rem', color: '#1e293b' }}>{svc}</span>
                  </label>
                );
              })}
            </div>
          )}

          <h2 style={{ color: '#1e3a5f', marginTop: 24 }}>Additional Notes (optional)</h2>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            data-testid="cna-input-notes"
            placeholder="Allergies, mobility concerns, preferred caregiver gender, gate codes, parking, etc."
            rows={4}
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
          />

          {/* Agreement */}
          <h2 style={{ color: '#1e3a5f', marginTop: 28 }}><ClipboardCheck size={20} style={{ verticalAlign: '-3px', marginRight: 6 }} />Online Service Agreement</h2>
          <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 10, padding: 14, fontSize: '0.88rem', color: '#334155', maxHeight: 220, overflowY: 'auto', lineHeight: 1.5 }}>
            <p><b>By proceeding, the Client agrees:</b></p>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              <li>Rate: <b>${config.hourly_rate_usd}/hour</b>. All hours paid in full <b>up front</b>. No services dispatched until payment clears.</li>
              <li><b>{config.min_hours}-hour minimum</b> per booking; <b>{config.advance_notice_hours}-hour advance notice</b> required.</li>
              <li>Cancellations &gt; 24 hrs before start: refunded minus $25 admin fee. Inside 24 hrs: <b>non-refundable</b>. Once dispatched: <b>non-refundable</b>.</li>
              <li>Services are <b>non-medical companion / personal-care only</b>. Caregivers do not diagnose, prescribe, or administer medications.</li>
              <li><b>INDEMNIFICATION:</b> Client agrees to indemnify, defend, and hold harmless MedCare Nursing Institute &amp; Training Academy, its officers, owners, employees, contractors, caregivers, and affiliates from any and all claims, demands, liabilities, damages, judgments, settlements, costs, and attorney's fees arising out of or connected with the services, the acts or omissions of the Client or third parties, any injury/illness/death/property damage during service, or any allegation that the caregiver acted beyond the non-medical scope.</li>
              <li><b>Liability cap:</b> total aggregate liability shall not exceed the amount paid in the 30 days preceding the claim. No incidental, consequential, or punitive damages.</li>
              <li>Governed by Tennessee law; venue: Shelby County, TN.</li>
              <li>Typing your full legal name below constitutes a legally binding electronic signature under the U.S. E-SIGN Act.</li>
            </ul>
            <p style={{ marginBottom: 0, marginTop: 10, fontStyle: 'italic', color: '#475569' }}>
              The full, signed Service Agreement (PDF) is generated upon checkout and downloadable from your confirmation page.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginTop: 14 }}>
            <Field
              label="Electronic Signature — type your full legal name"
              name="typed_signature"
              value={form.typed_signature}
              onChange={handleChange}
              testid="cna-input-signature"
              required
            />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.92rem', color: '#1e293b', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="agreed"
                checked={form.agreed}
                onChange={handleChange}
                data-testid="cna-agree-checkbox"
                style={{ marginTop: 4 }}
              />
              <span>
                I have read and agree to the <b>Service Agreement, indemnity terms, and refund policy</b>.
                I authorize MedCare to charge my payment method for the total below.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="cna-submit-checkout"
            style={{
              marginTop: 22, width: '100%', padding: '14px 22px',
              background: submitting ? '#94a3b8' : 'linear-gradient(135deg,#16a34a,#15803d)',
              color: 'white', border: 'none', borderRadius: 10, fontSize: '1.05rem',
              fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
              boxShadow: '0 6px 16px -10px rgba(22,163,74,0.6)',
            }}
          >
            {submitting ? 'Starting Checkout…' : `Pay $${totalDollars.toLocaleString()} & Confirm Booking`}
          </button>
        </form>

        {/* SIDEBAR SUMMARY */}
        <aside style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
          <div style={{ background: 'white', padding: 20, borderRadius: 14, boxShadow: '0 4px 14px -8px rgba(15,23,42,0.18)' }}>
            <h3 style={{ color: '#1e3a5f', marginTop: 0, fontSize: '1.05rem' }}>Booking Summary</h3>
            <Row icon={<DollarSign size={16} color="#16a34a" />} label="Rate" value={`$${config.hourly_rate_usd}.00 / hour`} />
            <Row icon={<Clock size={16} color="#1e40af" />} label="Hours" value={`${form.hours || 0} hr`} />
            <Row icon={<HeartPulse size={16} color="#dc2626" />} label="Services" value={`${form.services.length} selected`} />
            <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '10px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.2rem', color: '#0f172a' }}>
              <span>Total (paid up front)</span>
              <span data-testid="cna-total-display">${totalDollars.toLocaleString()}</span>
            </div>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: '0.8rem' }}>
              <ShieldCheck size={16} color="#16a34a" /> Secured by Stripe · PCI-DSS compliant
            </div>
          </div>

          <div style={{ marginTop: 16, background: '#fef3c7', border: '1px solid #fde68a', padding: 14, borderRadius: 10, color: '#78350f', fontSize: '0.85rem', lineHeight: 1.5 }}>
            <b>Important:</b> All bookings require {config.advance_notice_hours}-hour advance notice and a {config.min_hours}-hour minimum.
            Cancellations inside 24 hours are non-refundable. Once a caregiver is dispatched, all fees are non-refundable.
          </div>
        </aside>
      </div>

      <style>{`
        @media (max-width: 880px) {
          .hire-cna-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: '0.95rem',
  background: 'white',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: 4,
};

const hintStyle = {
  fontSize: '0.78rem',
  color: '#64748b',
  marginTop: 4,
};

const Field = ({ label, name, value, onChange, testid, type = 'text', required }) => (
  <div>
    <label style={labelStyle}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      style={inputStyle}
      required={required}
    />
  </div>
);

const Row = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.9rem', color: '#334155' }}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{icon}{label}</span>
    <b>{value}</b>
  </div>
);

export default HireCNAPage;
